// ── Contract issue resolution & execution gate (2026-09-23) ──────────────────
// The step between internal contract review and signing:
//   1. departments sign off; a reviewer logs each ask as a separate issue,
//      flagged "must resolve before signing" or "note for the project team"
//      (always must-resolve on a Rejected row);
//   2. Sales records the outcome agreed with the issuer on each must-resolve
//      issue (status proposed) — the raising department is emailed;
//   3. that department accepts it or reopens it with a note;
//   4. once nothing is Pending / Rejected and every must-resolve issue is
//      accepted, Sales marks the contract executed on §3 (signed date, signer,
//      the uploaded Executed Contract) — sp_MarkContractExecuted;
//   5. Submit for Job Number requires step 4 (sp_SubmitForJobNumber).
// The procs enforce the same rules; this module drives the page's gate, the
// Next Required Action, the payloads and the notification emails. Pure.

export const EXECUTED_CONTRACT_DOC_TYPE = "Executed Contract";

export const ISSUE_OUTCOMES = [
  { key: "issuer_accepted", label: "Issuer accepted our change" },
  { key: "compromise",      label: "Compromise agreed" },
  { key: "withdrawn",       label: "Withdrawn — no change needed" },
  { key: "risk_accepted",   label: "Issuer declined — risk accepted as-is" },
];
export const outcomeLabel = (key) => ISSUE_OUTCOMES.find(o => o.key === key)?.label || key || "—";

export const ISSUE_STATUS = {
  noted:    { label: "Note for project team", color: "#475569", bg: "#f1f5f9" },
  open:     { label: "Open — needs resolution", color: "#b91c1c", bg: "#fee2e2" },
  reopened: { label: "Reopened", color: "#b91c1c", bg: "#fee2e2" },
  proposed: { label: "Outcome recorded — awaiting acceptance", color: "#92400e", bg: "#fef3c7" },
  accepted: { label: "Accepted", color: "#166534", bg: "#dcfce7" },
};

export const isMustResolve = (i) => i?.MustResolve === true || i?.MustResolve === 1;

export function issuesByReview(issues = []) {
  const map = new Map();
  for (const i of issues || []) {
    if (!map.has(i.ReviewID)) map.set(i.ReviewID, []);
    map.get(i.ReviewID).push(i);
  }
  return map;
}

const sameEmail = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

// The raising department's reviewer: the row's assignee or whoever signed it.
export function canActForReview(review, user, isAdmin = false) {
  if (isAdmin) return true;
  return sameEmail(review?.AssignedEmail, user?.email) || sameEmail(review?.ReviewerEmail, user?.email);
}
// Sales: the turnover's salesperson or the §3 owner.
export function canResolveIssues(turnover, section3, user, isAdmin = false) {
  if (isAdmin) return true;
  return sameEmail(turnover?.SalespersonEmail, user?.email) || sameEmail(section3?.AssignedEmail, user?.email);
}

// Where the contract stands on the way to signing. `stage` is the first thing
// still outstanding: signoff → resolve → accept → rejected → execute → submit.
export function contractGate({ reviews = [], issues = [], turnover = {}, documents = [], section3 = null } = {}) {
  const pending  = reviews.filter(r => r.Status === "Pending");
  const rejected = reviews.filter(r => r.Status === "Rejected");
  const must     = (issues || []).filter(isMustResolve);
  const toResolve = must.filter(i => i.Status === "open" || i.Status === "reopened");
  const toAccept  = must.filter(i => i.Status === "proposed");
  const accepted  = must.filter(i => i.Status === "accepted");
  // A Rejected department must log what has to change before Sales can work it.
  const rejectedWithoutIssue = rejected.filter(r => !must.some(i => i.ReviewID === r.ReviewID));
  const executed = !!turnover?.ContractExecutedDate;
  const executedDocs = (documents || []).filter(d =>
    d.DocType === EXECUTED_CONTRACT_DOC_TYPE && (!section3 || d.SectionID === section3.SectionID));

  const blockers = [];
  for (const r of pending) blockers.push({ kind: "signoff", reviewId: r.ReviewID, text: `${r.Department} sign-off` });
  for (const r of rejectedWithoutIssue) blockers.push({ kind: "log_issue", reviewId: r.ReviewID, text: `${r.Department} rejected — log what must change` });
  for (const i of toResolve) blockers.push({ kind: "resolve", issueId: i.IssueID, reviewId: i.ReviewID, text: `Resolve with the issuer: ${i.Description}` });
  for (const i of toAccept) blockers.push({ kind: "accept", issueId: i.IssueID, reviewId: i.ReviewID, text: `Awaiting acceptance: ${i.Description}` });
  const rejectedReady = rejected.filter(r => !rejectedWithoutIssue.includes(r));
  for (const r of rejectedReady) {
    const own = must.filter(i => i.ReviewID === r.ReviewID);
    if (own.every(i => i.Status === "accepted")) blockers.push({ kind: "update_status", reviewId: r.ReviewID, text: `${r.Department}: outcomes accepted — change the status from Rejected` });
  }

  const readyToExecute = !executed && blockers.length === 0;
  let stage;
  if (pending.length || rejectedWithoutIssue.length) stage = "signoff";
  else if (toResolve.length) stage = "resolve";
  else if (toAccept.length) stage = "accept";
  else if (rejected.length) stage = "rejected";
  else if (!executed) stage = "execute";
  else stage = "submit";

  return {
    stage, blockers, readyToExecute, executed,
    pending, rejected, toResolve, toAccept, accepted, mustResolveCount: must.length,
    executedDocs,
  };
}

// ── payloads (callers JSON.stringify + '' escape) ─────────────────────────────
const who = (user) => ({ by: user?.name || "", by_email: user?.email || "" });
export const createIssuePayload = (turnoverId, reviewId, description, mustResolve, user) =>
  ({ action: "create", turnover_id: turnoverId, review_id: reviewId, description, must_resolve: mustResolve ? 1 : 0, ...who(user) });
export const deleteIssuePayload = (turnoverId, issueId) => ({ action: "delete", turnover_id: turnoverId, issue_id: issueId });
export const resolveIssuePayload = (turnoverId, issueId, outcome, notes, user) =>
  ({ action: "resolve", turnover_id: turnoverId, issue_id: issueId, outcome, notes, ...who(user) });
export const acceptIssuePayload = (turnoverId, issueId, user) => ({ action: "accept", turnover_id: turnoverId, issue_id: issueId, ...who(user) });
export const reopenIssuePayload = (turnoverId, issueId, notes, user) => ({ action: "reopen", turnover_id: turnoverId, issue_id: issueId, notes, ...who(user) });
export const markExecutedPayload = (turnoverId, { executedDate, signedBy, documentId }, user) =>
  ({ turnover_id: turnoverId, executed_date: executedDate, signed_by: signedBy, document_id: documentId, recorded_by: user?.name || "" });
export const clearExecutedPayload = (turnoverId) => ({ turnover_id: turnoverId, clear: 1 });

// ── job-number exception (2026-10-27) ─────────────────────────────────────────
// An administrator may request the job number before the executed contract
// exists ("documents to follow") — sp_SubmitForJobNumberException, admin-only on
// the SQL proxy. It waives ONLY the executed-contract requirement (sign-offs and
// must-resolve issues still gate it) and stays OUTSTANDING until the signed
// contract is recorded.
export const jobNumberExceptionPayload = (turnoverId, { reason, expectedDate }, user) =>
  ({ turnover_id: turnoverId, reason: String(reason || "").trim(), expected_date: expectedDate, by: user?.name || "" });

export const exceptionRecorded = (t) => !!t?.JobNumberExceptionAt;
export const exceptionOutstanding = (t) => exceptionRecorded(t) && !t?.ContractExecutedDate;

export function exceptionDateText(d) {
  const s = String(d || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "—";
  return new Date(s + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

// The amber block Accounting (and anyone reading the package) sees. Escaped.
export function exceptionNoteHtml({ reason, expectedDate, by }) {
  return `<div style="background:#fef3c7;border:1px solid #f59e0b;border-radius:6px;padding:10px 14px;margin:0 0 16px;font-size:13px;color:#92400e">
      <div style="font-weight:700;margin-bottom:4px">Requested without an executed contract — documents to follow</div>
      <div><strong>Why:</strong> ${esc(reason)}</div>
      <div><strong>Executed contract expected:</strong> ${esc(exceptionDateText(expectedDate))}</div>
      <div><strong>Approved by:</strong> ${esc(by || "—")}</div>
    </div>`;
}

// ── notification emails ───────────────────────────────────────────────────────
export const PLATFORM_BASE_URL = "https://estimating.cleanspaceus.com";
function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function frame(title, project, bodyHtml, url, cta) {
  return `
<div style="font-family:Arial,sans-serif;max-width:680px;color:#111">
  <div style="background:#1e3a5f;padding:18px 26px;border-radius:8px 8px 0 0">
    <div style="color:#fff;font-size:18px;font-weight:700">${title}</div>
    <div style="color:#93c5fd;font-size:13px;margin-top:4px">${project}</div>
  </div>
  <div style="border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;padding:22px 26px;font-size:14px;line-height:1.55">
    ${bodyHtml}
    <div style="margin:18px 0 0">
      <a href="${esc(url)}" style="display:inline-block;background:#1e3a5f;color:#fff;padding:10px 22px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none">${cta}</a>
    </div>
  </div>
</div>`;
}
const issueBox = (i, extra = "") => `
    <div style="background:#f8f9fb;border:1px solid #e5e7eb;border-radius:6px;padding:12px 16px;margin:0 0 12px">
      <div style="font-size:12px;color:#6b7280;margin-bottom:4px">Your issue</div>
      <div style="font-weight:600;margin-bottom:8px">${esc(i.Description)}</div>${extra}
    </div>`;

// To the raising department: Sales recorded an outcome — accept or reopen.
export function outcomeRecordedEmail({ turnover, review, issue, outcome, notes, resolvedBy }) {
  const project = esc(turnover?.ProjectName) || "the turnover";
  const body = `
    <p style="margin:0 0 14px">${esc(resolvedBy) || "Sales"} recorded the outcome agreed with the issuer for an issue ${esc(review?.Department)} raised on the <strong>${project}</strong> contract. Please accept it or reopen it.</p>
    ${issueBox(issue, `
      <div style="font-size:12px;color:#6b7280;margin-bottom:2px">Outcome</div>
      <div style="margin-bottom:8px"><strong>${esc(outcomeLabel(outcome))}</strong></div>
      <div style="font-size:12px;color:#6b7280;margin-bottom:2px">What was agreed</div>
      <div style="white-space:pre-wrap">${esc(notes)}</div>`)}
    <p style="margin:0">On Section 2, find the issue under <strong>Contract Issues</strong> and click <strong>Accept</strong>, or <strong>Reopen</strong> with a note saying what is still not acceptable. The contract cannot be signed until every must-resolve issue is accepted.</p>`;
  return {
    subject: `Contract issue ready for your acceptance — ${turnover?.ProjectName || "Turnover"} (${review?.Department || "Contract Review"})`,
    html: frame("Contract issue — outcome recorded", project, body, `${PLATFORM_BASE_URL}/turnover/${Number.parseInt(turnover?.TurnoverID, 10) || ""}?section=2`, "Open and accept or reopen →"),
  };
}

// To Sales: a department reopened an outcome.
export function issueReopenedEmail({ turnover, review, issue, note, reopenedBy }) {
  const project = esc(turnover?.ProjectName) || "the turnover";
  const body = `
    <p style="margin:0 0 14px">${esc(reopenedBy) || esc(review?.Department)} did not accept the negotiated outcome for a ${esc(review?.Department)} issue on the <strong>${project}</strong> contract. It needs another round with the issuer.</p>
    ${issueBox(issue, `
      <div style="font-size:12px;color:#6b7280;margin-bottom:2px">Why it was reopened</div>
      <div style="white-space:pre-wrap">${esc(note)}</div>`)}
    <p style="margin:0">Resolve it with the issuer, then record the new outcome on Section 2 under <strong>Contract Issues</strong>.</p>`;
  return {
    subject: `Contract issue reopened — ${turnover?.ProjectName || "Turnover"} (${review?.Department || "Contract Review"})`,
    html: frame("Contract issue reopened", project, body, `${PLATFORM_BASE_URL}/turnover/${Number.parseInt(turnover?.TurnoverID, 10) || ""}?section=2`, "Open the contract issues →"),
  };
}

// To Sales: the last must-resolve issue was accepted — the contract can be signed.
export function readyToSignEmail({ turnover, acceptedCount }) {
  const project = esc(turnover?.ProjectName) || "the turnover";
  const body = `
    <p style="margin:0 0 14px">Every department has signed off on the <strong>${project}</strong> contract${acceptedCount ? ` and all ${acceptedCount} negotiated issue${acceptedCount === 1 ? " has" : "s have"} been accepted` : ""}. The contract can now be signed.</p>
    <p style="margin:0">Once it is signed, upload it to Section 3 as type <strong>Executed Contract</strong>, click <strong>Mark contract executed</strong> (signed date and who signed for CleanSpace), then <strong>Submit for Job Number</strong>.</p>`;
  return {
    subject: `Ready to sign — ${turnover?.ProjectName || "Turnover"} contract review complete`,
    html: frame("Contract ready to sign", project, body, `${PLATFORM_BASE_URL}/turnover/${Number.parseInt(turnover?.TurnoverID, 10) || ""}?section=3`, "Open Section 3 →"),
  };
}
